import { START_OVER_ID, START_OVER_LABEL } from "@sagot/constants";
import type { Definition, Step, StepTranslation, Translations } from "./types";

/** Which visitor-facing text of a step a translation holds (backend `services/workflows/translate.py`). */
export type StringKind = "text" | "button" | "field_label" | "label";

/** One text the visitor reads, in the workflow's primary language. */
export interface SourceString {
  /** `<step id>.text`, `<step id>.button.<button id>` or `<step id>.field_label`: unique within a workflow. */
  key: string;
  stepId: string;
  path: string;
  kind: StringKind;
  buttonId?: string;
  text: string;
}

function stringsOf(path: string, step: Step): SourceString[] {
  switch (step.type) {
    case "send_message":
    case "ask_rating":
    case "collect_reply":
    case "verify_email":
      return [{ key: `${step.id}.text`, stepId: step.id, path, kind: "text", text: step.text }];
    case "reply_buttons":
      return [
        { key: `${step.id}.text`, stepId: step.id, path, kind: "text", text: step.text },
        ...step.buttons.map((button) => ({ key: `${step.id}.button.${button.id}`, stepId: step.id, path, kind: "button" as const, buttonId: button.id, text: button.label })),
        ...(step.start_over ? [{ key: `${step.id}.button.${START_OVER_ID}`, stepId: step.id, path, kind: "button" as const, buttonId: START_OVER_ID, text: START_OVER_LABEL.en }] : []),
      ];
    case "collect_data":
      return [
        { key: `${step.id}.text`, stepId: step.id, path, kind: "text", text: step.text },
        { key: `${step.id}.field_label`, stepId: step.id, path, kind: "field_label", text: step.field.label },
      ];
    case "show_article":
      return [
        { key: `${step.id}.text`, stepId: step.id, path, kind: "text", text: step.text ?? "" },
        { key: `${step.id}.label`, stepId: step.id, path, kind: "label", text: step.label ?? "" },
      ];
    default:
      return [];
  }
}

/** Every text the visitor reads, in path and step order; texts still empty in the builder are left out (nothing to translate yet). */
export function translatableStrings(definition: Definition): SourceString[] {
  return Object.keys(definition.paths)
    .sort()
    .flatMap((path) => (definition.paths[path]?.steps ?? []).flatMap((step) => stringsOf(path, step)))
    .filter((string) => string.text.trim() !== "");
}

/** The translation of one text in one language, or `""` when it falls back to the primary text. */
export function translationOf(definition: Definition, language: string, string: SourceString): string {
  const found = definition.translations?.[language]?.[string.stepId];
  if (!found) return "";
  if (string.kind === "button") return string.buttonId ? (found.buttons?.[string.buttonId] ?? "") : "";
  if (string.kind === "field_label") return found.field_label ?? "";
  if (string.kind === "label") return found.label ?? "";
  return found.text ?? "";
}

function withText(found: StepTranslation, string: SourceString, value: string): StepTranslation {
  if (string.kind === "text") return { ...found, text: value };
  if (string.kind === "field_label") return { ...found, field_label: value };
  if (string.kind === "label") return { ...found, label: value };
  return { ...found, buttons: { ...found.buttons, [string.buttonId ?? ""]: value } };
}

/** Drops empty values and empty containers: the server refuses a blank translation and a step without any text. */
function compact(found: StepTranslation): StepTranslation | null {
  const buttons = Object.fromEntries(Object.entries(found.buttons ?? {}).filter(([, label]) => label.trim() !== ""));
  const next: StepTranslation = {
    ...(found.text?.trim() ? { text: found.text } : {}),
    ...(Object.keys(buttons).length > 0 ? { buttons } : {}),
    ...(found.field_label?.trim() ? { field_label: found.field_label } : {}),
    ...(found.label?.trim() ? { label: found.label } : {}),
  };
  return Object.keys(next).length > 0 ? next : null;
}

const without = <T>(record: Readonly<Record<string, T>>, key: string): Record<string, T> => Object.fromEntries(Object.entries(record).filter(([name]) => name !== key));

function withTranslations(definition: Definition, translations: Translations): Definition {
  const base: Definition = { settings: definition.settings, paths: definition.paths, ...(definition.notes ? { notes: definition.notes } : {}) };
  return Object.keys(translations).length > 0 ? { ...base, translations } : base;
}

/** The definition with one text translated (an empty value removes it, so the primary text shows again). */
export function setTranslation(definition: Definition, language: string, string: SourceString, value: string): Definition {
  const forLanguage = definition.translations?.[language] ?? {};
  const updated = compact(withText(forLanguage[string.stepId] ?? {}, string, value));
  const steps = updated ? { ...forLanguage, [string.stepId]: updated } : without(forLanguage, string.stepId);
  const others = without(definition.translations ?? {}, language);
  return withTranslations(definition, Object.keys(steps).length > 0 ? { ...others, [language]: steps } : others);
}

/** What the draft route returns for one step: texts it did not draft are `null` or left out. */
export interface DraftedTexts {
  text?: string | null;
  buttons?: Record<string, string> | null;
  field_label?: string | null;
  label?: string | null;
}

/** Merges drafted translations (`POST .../translations/draft`) into the definition without touching texts the author already wrote. */
export function mergeDraft(definition: Definition, language: string, drafted: Readonly<Record<string, DraftedTexts>>): { definition: Definition; keys: string[] } {
  let next = definition;
  const keys: string[] = [];
  for (const string of translatableStrings(definition)) {
    const found = drafted[string.stepId];
    if (!found || translationOf(definition, language, string) !== "") continue;
    const value = string.kind === "button" ? (string.buttonId ? found.buttons?.[string.buttonId] : undefined) : string.kind === "field_label" ? found.field_label : string.kind === "label" ? found.label : found.text;
    if (!value?.trim()) continue;
    next = setTranslation(next, language, string, value);
    keys.push(string.key);
  }
  return { definition: next, keys };
}

/** How many texts still fall back to the primary language. */
export const missingCount = (definition: Definition, language: string): number =>
  translatableStrings(definition).filter((string) => translationOf(definition, language, string) === "").length;

/**
 * Removes translations of steps and buttons that no longer exist: the server refuses a translation that names a missing step
 * or button. Returns the same object when nothing changed.
 */
export function pruneTranslations(definition: Definition): Definition {
  const translations = definition.translations;
  if (!translations) return definition;
  const steps = new Map<string, Step>();
  for (const path of Object.values(definition.paths)) for (const step of path.steps) steps.set(step.id, step);
  const kept: Translations = {};
  for (const [language, byStep] of Object.entries(translations)) {
    const nextByStep: Record<string, StepTranslation> = {};
    for (const [stepId, found] of Object.entries(byStep)) {
      const step = steps.get(stepId);
      if (!step) continue;
      const buttons = step.type === "reply_buttons" ? Object.fromEntries(Object.entries(found.buttons ?? {}).filter(([id]) => step.buttons.some((button) => button.id === id) || (step.start_over === true && id === START_OVER_ID))) : {};
      const next = compact({ ...found, buttons });
      if (next) nextByStep[stepId] = next;
    }
    if (Object.keys(nextByStep).length > 0) kept[language] = nextByStep;
  }
  const same = JSON.stringify(kept) === JSON.stringify(translations);
  return same ? definition : withTranslations(definition, kept);
}
